import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MapPin, Clock, HeartHandshake, GraduationCap, ShieldPlus, Users, ArrowRight, Send, X, UploadCloud, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import Reveal from '../components/Reveal';
import { PageHero } from './Products';
import { JOBS, CAREER_BENEFITS, HERO_IMAGES } from '../mock';
import api, { apiErrorMessage } from '../lib/api';

const BENEFIT_ICONS = [HeartHandshake, GraduationCap, ShieldPlus, Users];

export default function Career() {
  const { toast } = useToast();
  const [applyJob, setApplyJob] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [resume, setResume] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const openApply = (job) => { setApplyJob(job); setForm({ name: '', email: '', phone: '', message: '' }); setResume(''); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/submissions', {
        type: 'career', name: form.name, email: form.email, phone: form.phone,
        jobTitle: applyJob.title, resume, message: form.message,
      });
      toast({ title: 'Application submitted!', description: `Thanks for applying to ${applyJob.title}. We’ll be in touch soon.` });
      setApplyJob(null);
    } catch (err) {
      toast({ title: 'Could not submit application', description: apiErrorMessage(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#070a12]">
      <PageHero title="Careers" crumb="Careers" image={HERO_IMAGES[0]} />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="section-eyebrow fire-text"><Briefcase className="w-4 h-4 text-primary" /> Join the Mission</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl mt-4 text-white">Build a Career That Saves Lives</h2>
            <p className="text-gray-400 text-lg mt-5 leading-relaxed">At Closefire, your work protects people and property from fire every single day. Join a passionate team of engineers, technicians and problem-solvers building a safer India.</p>
          </Reveal>
          <Reveal delay={120} className="img-zoom rounded-3xl overflow-hidden grad-border">
            <img src={HERO_IMAGES[2]} alt="Careers at Closefire" className="w-full h-[420px] object-cover" />
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-[#05070d]">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CAREER_BENEFITS.map((b, i) => {
            const Icon = BENEFIT_ICONS[i];
            return (
              <Reveal key={b.title} delay={i * 90} className="text-center">
                <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent text-white mb-4"><Icon className="w-7 h-7" /></span>
                <h3 className="font-heading font-bold text-lg text-white">{b.title}</h3>
                <p className="text-sm text-gray-400 mt-2">{b.desc}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal className="mb-12"><div className="section-eyebrow fire-text">Open Positions</div><h2 className="font-heading font-extrabold text-4xl text-white mt-4">{JOBS.length} Roles Currently Open</h2></Reveal>
          <div className="space-y-4">
            {JOBS.map((job, i) => (
              <Reveal key={job.id} delay={i * 60}>
                <div className="glass glass-hover rounded-3xl p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-xl text-white">{job.title}</h3>
                      <Badge className="bg-primary/15 text-primary border-none">{job.dept}</Badge>
                    </div>
                    <p className="text-sm text-gray-400 mt-2 max-w-2xl">{job.desc}</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-gray-500 font-heading font-semibold">
                      <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" /> {job.location}</span>
                      <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-primary" /> {job.type}</span>
                      <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-primary" /> {job.exp}</span>
                    </div>
                  </div>
                  <Button onClick={() => openApply(job)} className="gap-2 bg-gradient-to-r from-primary to-accent shrink-0">Apply Now <ArrowRight className="w-4 h-4" /></Button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Apply modal */}
      <AnimatePresence>
        {applyJob && (
          <motion.div className="fixed inset-0 z-[100] grid place-items-center p-4 bg-black/80 backdrop-blur-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setApplyJob(null)}>
            <motion.div className="relative w-full max-w-lg glass rounded-3xl p-8 max-h-[90vh] overflow-y-auto" initial={{ scale: 0.92, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }} transition={{ type: 'spring', damping: 24, stiffness: 260 }} onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setApplyJob(null)} className="absolute top-5 right-5 text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              <div className="section-eyebrow fire-text">Apply</div>
              <h3 className="font-heading font-bold text-2xl text-white mt-2">{applyJob.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{applyJob.location} · {applyJob.type}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <Input name="name" value={form.name} onChange={change} required placeholder="Full name" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                <Input name="email" type="email" value={form.email} onChange={change} required placeholder="Email" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                <Input name="phone" value={form.phone} onChange={change} required placeholder="Phone" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                <label className={`flex items-center gap-3 h-12 px-4 rounded-lg border cursor-pointer transition-colors ${resume ? 'border-primary/60 text-white' : 'border-white/10 text-gray-500 hover:border-white/25'}`}>
                  {resume ? <Check className="w-4 h-4 text-primary" /> : <UploadCloud className="w-4 h-4" />}
                  <span className="text-sm font-heading font-semibold truncate">{resume || 'Upload resume (PDF / DOC)'}</span>
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files?.[0]?.name || '')} />
                </label>
                <Textarea name="message" value={form.message} onChange={change} placeholder="Cover note (optional)" rows={3} className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none" />
                <Button type="submit" disabled={submitting} size="lg" className="w-full gap-2 h-12 bg-gradient-to-r from-primary to-accent hover:opacity-90">{submitting ? 'Submitting…' : <>Submit Application <Send className="w-4 h-4" /></>}</Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
